import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Typography from '../common/Typography';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import { createPrivateSession } from '../../services/privateSessionService';
import {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
  resolveEndTimeForStartChange,
  getEndTimeMin,
} from '../../utils/sessionTimeUtils';

const CreatePrivateSessionModal = ({ isOpen, onClose, currentUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    startTime: DEFAULT_SESSION_START_TIME,
    endTime: DEFAULT_SESSION_END_TIME,
    location: '',
    description: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Session name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const sessionData = {
        name: formData.name.trim(),
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime || null,
        location: formData.location.trim() || null,
        description: formData.description.trim() || null,
      };

      const sessionId = await createPrivateSession(sessionData, currentUser.id);
      navigate(`/session/${sessionId}`);
    } catch (err) {
      console.error('Error creating private session:', err);
      setError(err.message || 'Failed to create private session');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartTimeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      startTime: value,
      endTime: resolveEndTimeForStartChange(value, prev.endTime),
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!loading}>
      <ModalContent size="small" padding={false} className="overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border-medium p-4 sm:p-6">
          <Typography variant="h5" className="font-semibold">
            Create Private Session
          </Typography>
          <ModalCloseButton onClose={onClose} className="static" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Session Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
              placeholder="e.g., Friday Night Badminton"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Start Time *</label>
              <input
                type="time"
                step={60}
                value={formData.startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">End Time</label>
            <input
              type="time"
              step={60}
              min={getEndTimeMin(formData.startTime)}
              value={formData.endTime}
              onChange={(e) => handleInputChange('endTime', e.target.value)}
              className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
              placeholder="e.g., Community Center Court 1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full form-input px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface text-text-primary"
              rows={3}
              placeholder="Optional description for your session..."
            />
          </div>

          {error && (
            <div className="bg-danger-light border border-danger text-danger-dark px-4 py-3 rounded">{error}</div>
          )}

          <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:gap-3">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Session'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CreatePrivateSessionModal;
